import { useEffect, useState, useCallback } from "react";
import toast from "react-hot-toast";
import {
    FiCheck,
    FiX,
    FiCalendar,
    FiUser,
    FiRefreshCw,
    FiFilter,
    FiClock,
    FiCheckCircle,
    FiXCircle,
    FiShare2,
} from "react-icons/fi";
import leaveService from "../../services/leaveService";
import collegeService from "../../services/collegeService";
import { useApp } from "../../context/AppContext";

const STATUS_STYLE = {
    Pending: { bg: "#fef3c7", color: "#92400e", icon: FiClock, label: "Pending" },
    Approved: { bg: "#dcfce7", color: "#15803d", icon: FiCheckCircle, label: "Approved" },
    Rejected: { bg: "#fee2e2", color: "#dc2626", icon: FiXCircle, label: "Rejected" },
};

function RejectModal({ application, onClose, onConfirm, saving }) {
    const [remarks, setRemarks] = useState("");
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
                <div className="modal-header">
                    <h2 style={{ display: "flex", alignItems: "center", gap: 6, color: "#dc2626", fontSize: 16 }}>
                        <FiXCircle /> Reject Leave Application
                    </h2>
                    <button type="button" className="btn btn-icon" onClick={onClose}><FiX /></button>
                </div>
                <div className="modal-body">
                    <p style={{ fontSize: 13, marginBottom: 12 }}>
                        Rejecting leave request for <strong>{application.employee?.fullName}</strong> ({application.employee?.department || application.employee?.employeeCategory}).
                    </p>
                    <div style={{ background: "#f8fafc", padding: "8px 12px", borderRadius: 6, marginBottom: 12, fontSize: 12.5 }}>
                        <div><strong>Type:</strong> {application.leaveType?.name} ({application.totalDays} day{application.totalDays !== 1 ? "s" : ""})</div>
                        <div><strong>Dates:</strong> {application.fromDate} to {application.toDate}</div>
                        <div><strong>Reason:</strong> {application.reason}</div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Rejection Remarks (optional):</label>
                        <textarea
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                            rows={3}
                            placeholder="State reason for rejection (visible to employee)"
                            className="form-control"
                            maxLength={500}
                        />
                    </div>
                </div>
                <div className="modal-footer" style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", padding: "0.75rem 1.25rem", borderTop: "1px solid var(--border)" }}>
                    <button type="button" className="btn btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        style={{ background: "#dc2626", borderColor: "#dc2626" }}
                        disabled={saving}
                        onClick={() => onConfirm(remarks)}
                    >
                        {saving ? "Rejecting…" : "Confirm Rejection"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function LeaveApplicationsList() {
    const { user } = useApp();
    const isSuperAdmin = user?.role === "superadmin";

    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [statusFilter, setStatusFilter] = useState("Pending");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [selectedCollege, setSelectedCollege] = useState("");
    const [colleges, setColleges] = useState([]);
    const [actingId, setActingId] = useState(null);
    const [rejectTarget, setRejectTarget] = useState(null);

    // Load colleges if superadmin
    useEffect(() => {
        if (isSuperAdmin) {
            collegeService
                .getAll()
                .then((res) => setColleges(res.data?.data ?? res.data ?? []))
                .catch(() => {});
        }
    }, [isSuperAdmin]);

    const load = useCallback((isManual = false) => {
        if (isManual) setRefreshing(true);
        else setLoading(true);

        const params = {};
        if (statusFilter) params.status = statusFilter;
        if (categoryFilter) params.employeeCategory = categoryFilter;
        if (isSuperAdmin && selectedCollege) params.college = selectedCollege;

        leaveService
            .getAllApplications(params)
            .then((res) => setApplications(res.data?.data ?? res.data ?? []))
            .catch((err) => toast.error(err.response?.data?.message || err.message))
            .finally(() => {
                setLoading(false);
                setRefreshing(false);
            });
    }, [statusFilter, categoryFilter, isSuperAdmin, selectedCollege]);

    useEffect(() => {
        load();
    }, [load]);

    const handleCopyPublicLink = () => {
        const publicUrl = `${window.location.origin}/staff/leave-apply`;
        navigator.clipboard.writeText(publicUrl).then(
            () => toast.success("Public leave application link copied to clipboard!"),
            () => toast.error("Failed to copy link"),
        );
    };

    const handleApprove = async (application) => {
        setActingId(application._id);
        try {
            await leaveService.approveApplication(application._id);
            toast.success(`Application approved and synced to attendance!`);
            load(true);
        } catch (err) {
            toast.error(err.response?.data?.message || err.message);
        } finally {
            setActingId(null);
        }
    };

    const handleReject = async (remarks) => {
        setActingId(rejectTarget._id);
        try {
            await leaveService.rejectApplication(rejectTarget._id, { remarks });
            toast.success("Application rejected");
            setRejectTarget(null);
            load(true);
        } catch (err) {
            toast.error(err.response?.data?.message || err.message);
        } finally {
            setActingId(null);
        }
    };

    return (
        <div>
            <div className="page-header" style={{ flexWrap: "wrap", gap: "0.75rem" }}>
                <div>
                    <h1 className="page-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <FiCalendar size={22} color="var(--primary)" /> Leave Applications
                    </h1>
                    <p className="page-subtitle">
                        Review, approve or reject staff leave requests with automatic attendance synchronization
                    </p>
                </div>

                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
                    <button
                        type="button"
                        className="btn btn-outline"
                        onClick={() => load(true)}
                        disabled={refreshing}
                        title="Refresh applications list"
                    >
                        <FiRefreshCw size={13} className={refreshing ? "spin-icon" : ""} /> Refresh
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline"
                        onClick={handleCopyPublicLink}
                        title="Copy public link for non-teaching staff"
                        style={{ display: "flex", alignItems: "center", gap: 6 }}
                    >
                        <FiShare2 size={13} /> Copy Non-Teaching Public Link
                    </button>
                </div>
            </div>

            {/* Filter Bar */}
            <div className="card" style={{ marginBottom: "1rem", padding: "0.85rem 1rem" }}>
                <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <FiFilter size={14} color="var(--text-muted)" />
                        <span style={{ fontSize: 13, fontWeight: 600 }}>Filter:</span>
                    </div>

                    {isSuperAdmin && (
                        <div>
                            <select
                                value={selectedCollege}
                                onChange={(e) => setSelectedCollege(e.target.value)}
                                className="form-control"
                                style={{ fontSize: 13, minWidth: 200 }}
                            >
                                <option value="">All Colleges</option>
                                {colleges.map((c) => (
                                    <option key={c._id} value={c._id}>{c.name}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <label style={{ fontSize: 12.5, color: "var(--text-muted)" }}>Status:</label>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="form-control"
                            style={{ fontSize: 13, minWidth: 120 }}
                        >
                            <option value="">All Statuses</option>
                            <option value="Pending">Pending</option>
                            <option value="Approved">Approved</option>
                            <option value="Rejected">Rejected</option>
                        </select>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <label style={{ fontSize: 12.5, color: "var(--text-muted)" }}>Category:</label>
                        <select
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                            className="form-control"
                            style={{ fontSize: 13, minWidth: 140 }}
                        >
                            <option value="">All Categories</option>
                            <option value="Teaching">Teaching Staff</option>
                            <option value="Non Teaching">Non Teaching Staff</option>
                        </select>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="loading-center"><div className="spinner" />Loading applications…</div>
            ) : applications.length === 0 ? (
                <div className="card">
                    <div className="card-body" style={{ textAlign: "center", color: "var(--text-muted)", padding: "3rem 1rem" }}>
                        No leave applications match the selected criteria.
                    </div>
                </div>
            ) : (
                <div style={{ display: "grid", gap: "0.75rem" }}>
                    {applications.map((app) => {
                        const sc = STATUS_STYLE[app.status] || STATUS_STYLE.Pending;
                        const StatusIcon = sc.icon;
                        const isPending = app.status === "Pending";

                        return (
                            <div
                                key={app._id}
                                className="card"
                                style={{
                                    padding: "1.1rem 1.25rem",
                                    borderLeft: `4px solid ${sc.color}`,
                                    transition: "box-shadow 0.15s",
                                }}
                            >
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "1rem", flexWrap: "wrap" }}>
                                    <div>
                                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                                            <FiUser size={14} color="var(--primary)" />
                                            <strong style={{ fontSize: 14.5 }}>{app.employee?.fullName || "Employee"}</strong>
                                            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                                ({app.employee?.employeeCode || "—"}) · {app.employee?.department || "General"} · {app.employee?.designation || ""}
                                            </span>
                                            <span style={{
                                                fontSize: 11,
                                                fontWeight: 600,
                                                padding: "1px 6px",
                                                borderRadius: 4,
                                                background: app.employee?.employeeCategory === "Teaching" ? "#ede9fe" : "#f1f5f9",
                                                color: app.employee?.employeeCategory === "Teaching" ? "#7c3aed" : "#475569",
                                            }}>
                                                {app.employee?.employeeCategory || "Staff"}
                                            </span>
                                        </div>

                                        <div style={{ fontSize: 13, marginBottom: 4, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                            <span style={{ fontWeight: 700, color: "var(--text)" }}>
                                                {app.leaveType?.name || "Leave"}
                                                {app.leaveType?.code ? ` (${app.leaveType.code})` : ""}
                                            </span>
                                            <span>&middot;</span>
                                            <span style={{ fontWeight: 600 }}>
                                                {app.fromDate} to {app.toDate}
                                            </span>
                                            <span style={{ background: "#f1f5f9", padding: "1px 6px", borderRadius: 4, fontSize: 12, fontWeight: 700 }}>
                                                {app.totalDays} day{app.totalDays !== 1 ? "s" : ""}
                                            </span>
                                            {app.appliedVia === "Public" && (
                                                <span style={{ fontSize: 11, color: "#0284c7", background: "#e0f2fe", padding: "1px 6px", borderRadius: 4 }}>
                                                    via OTP Public Form
                                                </span>
                                            )}
                                            {app.academicYear?.label && (
                                                <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
                                                    ({app.academicYear.label})
                                                </span>
                                            )}
                                        </div>

                                        <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 4 }}>
                                            <strong>Reason:</strong> {app.reason}
                                        </div>

                                        {app.reviewRemarks && (
                                            <div style={{ fontSize: 12, marginTop: 6, padding: "4px 8px", background: "#f8fafc", borderRadius: 4, borderLeft: "2px solid var(--border)" }}>
                                                <strong>Admin Remarks:</strong> {app.reviewRemarks}
                                                {app.reviewedBy && (
                                                    <span style={{ color: "var(--text-muted)", marginLeft: 6 }}>
                                                        (by {app.reviewedBy.displayName || app.reviewedBy.username})
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
                                        <span style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 4,
                                            fontSize: 12,
                                            fontWeight: 700,
                                            padding: "3px 10px",
                                            borderRadius: 99,
                                            background: sc.bg,
                                            color: sc.color,
                                        }}>
                                            <StatusIcon size={12} /> {sc.label}
                                        </span>

                                        {isPending && (
                                            <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                                                <button
                                                    type="button"
                                                    className="btn btn-primary btn-sm"
                                                    disabled={actingId === app._id}
                                                    onClick={() => handleApprove(app)}
                                                    style={{ background: "#15803d", borderColor: "#15803d" }}
                                                >
                                                    <FiCheck size={12} /> Approve
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline btn-sm"
                                                    disabled={actingId === app._id}
                                                    onClick={() => setRejectTarget(app)}
                                                    style={{ color: "#dc2626", borderColor: "#dc2626" }}
                                                >
                                                    <FiX size={12} /> Reject
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {rejectTarget && (
                <RejectModal
                    application={rejectTarget}
                    onClose={() => setRejectTarget(null)}
                    onConfirm={handleReject}
                    saving={actingId === rejectTarget._id}
                />
            )}
        </div>
    );
}